import React, { useState, useEffect } from 'react';

const scoreColor = s => s >= 4 ? '#2E7D32' : s >= 3 ? '#1565C0' : s >= 2 ? '#F57F17' : '#C62828';
const TIER_LABELS = { me5:'ME5', me5_copilot:'ME5+Copilot', me5_a365:'ME5+A365', me7:'ME7' };
const TIER_COLORS = { me5:'#455A64', me5_copilot:'#1565C0', me5_a365:'#0078D4', me7:'#6A1B9A' };
const LAYER_LABELS = {
  governance:'Governance', security:'Security', observability:'Observability',
  data:'Data', infrastructure:'Infrastructure', agent_runtime:'Agent OS / Runtime',
  identity:'Identity & Access', network:'Network',
};

export default function FrameworkReference({ API }) {
  const [controls, setControls] = useState([]);
  const [objectives, setObjectives] = useState({});
  const [layers, setLayers] = useState([]);
  const [expandedDomain, setExpandedDomain] = useState(null);
  const [expandedControl, setExpandedControl] = useState(null);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState('domain'); // domain | layer | flat
  const [expandedStat, setExpandedStat] = useState(null); // domains|categories|controls|layers|tiers

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/controls`).then(r => r.ok ? r.json() : []),
      fetch(`${API}/api/reference/objectives`).then(r => r.ok ? r.json() : {}),
      fetch(`${API}/api/reference/agentic-layers`).then(r => r.ok ? r.json() : []),
    ]).then(([c, o, l]) => { setControls(c); setObjectives(o); setLayers(l); });
  }, [API]);

  const DOMAINS = [
    { id:'D1', name:'AI Governance & Policy', controls:10, color:'#1565C0' },
    { id:'D2', name:'AI Risk Management', controls:8, color:'#00796B' },
    { id:'D3', name:'Data Privacy & Integrity', controls:8, color:'#6A1B9A' },
    { id:'D4', name:'Infrastructure & Model Security', controls:12, color:'#E65100' },
    { id:'D5', name:'Workforce Readiness & AI Literacy', controls:7, color:'#1565C0' },
    { id:'D6', name:'Monitoring & Incident Response', controls:10, color:'#00796B' },
  ];

  // Filter by search
  const filtered = search.trim()
    ? controls.filter(c =>
        c.id.toLowerCase().includes(search.toLowerCase()) ||
        c.statement.toLowerCase().includes(search.toLowerCase()) ||
        c.category.toLowerCase().includes(search.toLowerCase()) ||
        (c.objective || '').toLowerCase().includes(search.toLowerCase()))
    : controls;

  // Group helpers
  const byDomain = (domainId) => filtered.filter(c => c.domain === domainId);
  const byLayer = (layerId) => filtered.filter(c => c.agentic_layer === layerId);

  const categoriesIn = (ctrls) => {
    const seen = [];
    ctrls.forEach(c => { if (!seen.find(x => x === c.category)) seen.push(c.category); });
    return seen;
  };

  const toggleDomain = (id) => setExpandedDomain(expandedDomain === id ? null : id);

  const hasSubs = (c) => (c.sub_controls||[]).length > 0;

  const ControlRow = ({ c }) => {
    const isOpen = expandedControl === c.id;
    const subs = c.sub_controls || [];
    return (
      <div style={{borderBottom:'1px solid #EEF0F3'}}>
        {/* ── Main control row ── */}
        <div onClick={() => setExpandedControl(isOpen ? null : c.id)} style={{
          display:'grid', gridTemplateColumns:'70px 1fr 80px 80px 24px',
          gap:8, alignItems:'center', padding:'8px 14px', cursor:'pointer',
          background: isOpen ? '#F8FAFC' : 'transparent',
        }}>
          <div>
            <span style={{fontSize:11,fontWeight:600,color:'#1565C0'}}>{c.id}</span>
            {hasSubs(c) && <div style={{fontSize:8,color:'#0078D4',fontWeight:500,marginTop:1}}>+{subs.length} sub</div>}
          </div>
          <div>
            <div style={{fontSize:11,color:'#0D1B2A',lineHeight:1.4}}>{c.statement}</div>
            <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>{c.evidence_required}</div>
          </div>
          <div style={{textAlign:'center'}}>
            <span style={{fontSize:9,padding:'2px 6px',borderRadius:6,
              background:(TIER_COLORS[c.licensing_tier]||'#455A64')+'14',
              color:TIER_COLORS[c.licensing_tier]||'#455A64',fontWeight:500}}>
              {TIER_LABELS[c.licensing_tier]||''}
            </span>
          </div>
          <div style={{textAlign:'center'}}>
            <span style={{fontSize:9,padding:'2px 6px',borderRadius:6,background:'#F1F3F5',color:'#455A64',fontWeight:500}}>
              {LAYER_LABELS[c.agentic_layer]||c.agentic_layer}
            </span>
          </div>
          <div style={{fontSize:14,color:'#B0BAC5',textAlign:'center',transform:isOpen?'rotate(90deg)':'none',transition:'transform 0.15s'}}>&#8250;</div>
        </div>

        {/* ── Sub-control rows (always visible, indented) ── */}
        {subs.map(sc => (
          <div key={sc.id} style={{
            display:'grid', gridTemplateColumns:'70px 1fr 80px 80px 24px',
            gap:8, alignItems:'center', padding:'6px 14px 6px 14px',
            background:'#FAFBFD', borderTop:'1px solid #F0F2F5',
          }}>
            <div style={{paddingLeft:12}}>
              <span style={{fontSize:10,fontWeight:600,color:'#0078D4'}}>{sc.id}</span>
            </div>
            <div>
              <div style={{fontSize:10,color:'#0D1B2A',lineHeight:1.4}}>{sc.statement}</div>
              <div style={{fontSize:9,color:'#7A8899',marginTop:1}}>{sc.evidence_required}</div>
              <div style={{fontSize:8,color:'#0078D4',marginTop:1}}>Parent: {c.id}</div>
            </div>
            <div style={{textAlign:'center'}}>
              <span style={{fontSize:8,padding:'2px 5px',borderRadius:6,
                background:(TIER_COLORS[sc.licensing_tier]||'#455A64')+'14',
                color:TIER_COLORS[sc.licensing_tier]||'#455A64',fontWeight:500}}>
                {TIER_LABELS[sc.licensing_tier]||''}
              </span>
            </div>
            <div></div>
            <div></div>
          </div>
        ))}

        {/* ── Expanded detail panel ── */}
        {isOpen && (
          <div style={{padding:'10px 14px 16px 84px',background:'#F8FAFC',fontSize:10,lineHeight:1.7}}>

            {/* Risk statement */}
            {c.risk_statement && (
              <div style={{padding:'8px 10px',background:'#FFEBEE',borderRadius:6,marginBottom:10,borderLeft:'3px solid #C62828'}}>
                <div style={{fontSize:9,color:'#C62828',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:2}}>Risk if missing</div>
                <div style={{color:'#455A64'}}>{c.risk_statement}</div>
              </div>
            )}

            {/* Criticality + metadata grid */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'6px 24px',marginBottom:10}}>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Domain:</span> {c.domain} — {c.domain_name}</div>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Category:</span> {c.category}</div>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Scoring source:</span> {c.source}</div>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Auto-scored:</span> {c.auto_scored ? 'Yes' : 'No'}</div>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Agentic layer:</span> {LAYER_LABELS[c.agentic_layer]||c.agentic_layer}</div>
              <div><span style={{color:'#7A8899',fontWeight:500}}>Licensing tier:</span> {TIER_LABELS[c.licensing_tier]||c.licensing_tier}</div>
              {c.criticality && (
                <div>
                  <span style={{color:'#7A8899',fontWeight:500}}>Criticality:</span>{' '}
                  <span style={{fontSize:9,padding:'2px 6px',borderRadius:6,fontWeight:600,
                    background:c.criticality==='critical'?'#FFEBEE':c.criticality==='high'?'#FFF3E0':'#F1F3F5',
                    color:c.criticality==='critical'?'#C62828':c.criticality==='high'?'#E65100':'#455A64',
                  }}>{c.criticality.charAt(0).toUpperCase()+c.criticality.slice(1)}</span>
                </div>
              )}
              {(c.atlas_techniques||[]).length > 0 && (
                <div style={{gridColumn:'1 / -1'}}>
                  <span style={{color:'#7A8899',fontWeight:500}}>MITRE ATLAS:</span>{' '}
                  {c.atlas_techniques.map(t => (
                    <span key={t} style={{display:'inline-block',padding:'1px 6px',margin:'0 3px 2px 0',borderRadius:4,background:'#FCE4EC',color:'#C62828',fontSize:9,fontWeight:600,fontFamily:'monospace'}}>{t}</span>
                  ))}
                </div>
              )}
            </div>

            {/* Framework cross-references */}
            {c.framework_refs && (
              <div style={{padding:'8px 10px',background:'#F0F6FF',borderRadius:6,marginBottom:10}}>
                <div style={{fontSize:9,color:'#1565C0',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:4}}>Framework alignment</div>
                <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                  {c.framework_refs.nist && <span style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'#E3F2FD',color:'#1565C0',fontWeight:500}}>NIST: {c.framework_refs.nist}</span>}
                  {c.framework_refs.iso && <span style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'#E8F5E9',color:'#2E7D32',fontWeight:500}}>ISO 42001: {c.framework_refs.iso}</span>}
                  {c.framework_refs.owasp && <span style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'#FFF3E0',color:'#E65100',fontWeight:500}}>OWASP: {c.framework_refs.owasp}</span>}
                  {c.framework_refs.atlas && <span style={{fontSize:9,padding:'2px 8px',borderRadius:4,background:'#FCE4EC',color:'#C62828',fontWeight:500}}>ATLAS: {c.framework_refs.atlas}</span>}
                </div>
              </div>
            )}

            {/* Scoring guidance */}
            {c.scoring_guidance && Object.keys(c.scoring_guidance).length > 0 && (
              <div style={{padding:'8px 10px',background:'#FAFBFC',borderRadius:6,marginBottom:10,border:'1px solid #E8ECF0'}}>
                <div style={{fontSize:9,color:'#455A64',fontWeight:600,textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:4}}>Scoring guidance</div>
                <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:'3px 10px'}}>
                  {['1','2','3','4','5'].map(s => c.scoring_guidance[s] ? (
                    <React.Fragment key={s}>
                      <span style={{fontWeight:700,fontSize:10,color:parseInt(s)>=4?'#2E7D32':parseInt(s)>=3?'#1565C0':parseInt(s)>=2?'#F57F17':'#C62828',minWidth:14}}>{s}</span>
                      <span style={{color:'#455A64',fontSize:10}}>{c.scoring_guidance[s]}</span>
                    </React.Fragment>
                  ) : null)}
                </div>
              </div>
            )}

            {/* Capability, product, policy */}
            {c.capability_required && (
              <div style={{marginBottom:4}}><span style={{color:'#1565C0',fontWeight:600}}>Capability required:</span> {c.capability_required}</div>
            )}
            {c.product_recommendation && (
              <div style={{marginBottom:4}}><span style={{color:'#00796B',fontWeight:600}}>Product recommendation:</span> {c.product_recommendation}</div>
            )}
            {c.policy_configuration && (
              <div style={{marginBottom:4}}><span style={{color:'#E65100',fontWeight:600}}>Policy configuration:</span> {c.policy_configuration}</div>
            )}
            {c.agent365_visibility && !c.agent365_visibility.startsWith('Not directly') && (
              <div style={{padding:'6px 8px',background:'#E3F2FD',borderRadius:4,marginTop:6}}>
                <span style={{color:'#0078D4',fontWeight:600}}>Agent 365 visibility:</span> {c.agent365_visibility}
              </div>
            )}

            {/* Related controls */}
            {(c.related_controls||[]).length > 0 && (
              <div style={{marginTop:6}}>
                <span style={{color:'#7A8899',fontWeight:500}}>Related controls:</span>{' '}
                {c.related_controls.map(rc => (
                  <span key={rc} style={{display:'inline-block',padding:'1px 6px',margin:'0 3px 2px 0',borderRadius:4,background:'#F1F3F5',color:'#1565C0',fontSize:9,fontWeight:600,cursor:'pointer'}}
                    onClick={(e) => { e.stopPropagation(); setExpandedControl(rc); }}>{rc}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:20}}>
        <div style={{fontSize:11,color:'#7A8899',lineHeight:1.6}}>
          The SAARA framework assesses AI security maturity across <strong>6 domains</strong>, <strong>30 control categories</strong>, and <strong>55 controls</strong> — mapped to
          NIST AI RMF 1.0, ISO 42001:2023, OWASP LLM Top 10, and MITRE ATLAS. Each control is mapped to an agentic architecture layer and Microsoft licensing tier.
        </div>
      </div>

      {/* Clickable stat cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:10,marginBottom:12}}>
        {[
          {key:'domains',val:6,label:'Domains',color:'#1565C0'},
          {key:'categories',val:30,label:'Categories',color:'#00796B'},
          {key:'controls',val:'55+5',label:'Controls + Sub',color:'#0D1B2A'},
          {key:'layers',val:8,label:'Agentic Layers',color:'#6A1B9A'},
          {key:'tiers',val:4,label:'Licensing Tiers',color:'#0078D4'},
        ].map(s => (
          <div key={s.key} className="stat-card" onClick={() => setExpandedStat(expandedStat === s.key ? null : s.key)}
            style={{cursor:'pointer',outline:expandedStat===s.key?`2px solid ${s.color}`:'none',outlineOffset:-2,transition:'outline 0.15s'}}>
            <div className="stat-val" style={{color:s.color}}>{s.val}</div>
            <div className="stat-sub">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Expandable detail panels */}
      {expandedStat === 'domains' && (
        <div style={{padding:'12px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>6 Assessment Domains</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
            {[
              {id:'D1',name:'AI Governance & Policy',desc:'AUP, AI system register, ethics framework, board accountability, regulatory mapping',color:'#1565C0',count:10},
              {id:'D2',name:'AI Risk Management',desc:'Risk assessment, impact assessment, enterprise risk integration, third-party AI risk, agentic governance',color:'#00796B',count:8},
              {id:'D3',name:'Data Privacy & Integrity',desc:'Training data governance, PII/DLP, data poisoning protection, RAG & embedding security',color:'#6A1B9A',count:8},
              {id:'D4',name:'Infrastructure & Model Security',desc:'Prompt injection defence, model supply chain, least-privilege agents, API security, output validation, red teaming',color:'#E65100',count:12},
              {id:'D5',name:'Workforce Readiness & AI Literacy',desc:'AI training, AUP acknowledgement, shadow AI management, human oversight',color:'#1565C0',count:7},
              {id:'D6',name:'Monitoring & Incident Response',desc:'Output monitoring, bias & fairness, IR playbook, incident management, ATLAS detection, continuous improvement',color:'#00796B',count:10},
            ].map(d => (
              <div key={d.id} style={{padding:'8px 12px',background:'#FAFBFC',borderRadius:8,border:'1px solid #E8ECF0'}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                  <span style={{fontSize:10,fontWeight:700,color:d.color}}>{d.id}</span>
                  <span style={{fontSize:10,fontWeight:600,color:'#0D1B2A'}}>{d.name}</span>
                  <span style={{fontSize:9,color:'#B0BAC5',marginLeft:'auto'}}>{d.count}</span>
                </div>
                <div style={{fontSize:9,color:'#7A8899',lineHeight:1.4}}>{d.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expandedStat === 'categories' && (
        <div style={{padding:'12px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>30 Control Categories</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:6}}>
            {Object.entries(objectives).map(([cat, obj]) => (
              <div key={cat} style={{padding:'6px 10px',background:'#FAFBFC',borderRadius:6,border:'1px solid #F0F2F5'}}>
                <div style={{fontSize:10,fontWeight:600,color:'#0D1B2A'}}>{cat}</div>
                <div style={{fontSize:9,color:'#7A8899',lineHeight:1.3,marginTop:2}}>{obj}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expandedStat === 'controls' && (
        <div style={{padding:'12px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:4}}>55 Controls + 5 Sub-controls</div>
          <div style={{fontSize:10,color:'#7A8899',lineHeight:1.6,marginBottom:8}}>
            Each control has: statement, evidence required, risk statement, scoring guidance (1-5), framework cross-references (NIST AI RMF, ISO 42001, OWASP LLM, MITRE ATLAS), criticality level, agentic layer mapping, licensing tier, and product recommendation. Sub-controls nest under parent controls and are scored together.
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:6}}>
            {[
              {label:'Auto-scored',count:filtered.filter(c=>c.auto_scored).length,color:'#2E7D32',desc:'Scored via Microsoft APIs (Sentinel, Defender, Purview, Entra)'},
              {label:'Document upload',count:filtered.filter(c=>c.source==='Upload').length,color:'#6A1B9A',desc:'Scored via AI document analysis'},
              {label:'Interview',count:filtered.filter(c=>c.source==='Interview').length,color:'#E65100',desc:'Scored via structured stakeholder questions'},
              {label:'Manual',count:filtered.filter(c=>c.source==='Manual').length,color:'#455A64',desc:'Scored by assessor judgment'},
              {label:'Critical',count:filtered.filter(c=>c.criticality==='critical').length,color:'#C62828',desc:'Must address within 30 days'},
              {label:'Sub-controls',count:5,color:'#0078D4',desc:'AI.4.4a, AI.4.6a, AI.4.6b, AI.4.10a, AI.5.7a'},
            ].map(s => (
              <div key={s.label} style={{padding:'8px 10px',background:'#FAFBFC',borderRadius:6,border:'1px solid #F0F2F5'}}>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <span style={{fontSize:16,fontWeight:700,color:s.color}}>{s.count}</span>
                  <span style={{fontSize:10,fontWeight:600,color:'#0D1B2A'}}>{s.label}</span>
                </div>
                <div style={{fontSize:9,color:'#7A8899',marginTop:2}}>{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expandedStat === 'layers' && (
        <div style={{padding:'12px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>8 Agentic Architecture Layers</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
            {(layers||[]).map(l => (
              <div key={l.id} style={{padding:'8px 10px',borderRadius:6,border:`1px solid ${l.color}22`,background:`${l.color}08`}}>
                <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:3}}>
                  <span style={{width:18,height:18,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:700,background:l.color+'18',color:l.color}}>{l.order}</span>
                  <span style={{fontSize:10,fontWeight:600,color:'#0D1B2A'}}>{l.name}</span>
                </div>
                <div style={{fontSize:9,color:'#7A8899',lineHeight:1.3}}>{l.m365}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {expandedStat === 'tiers' && (
        <div style={{padding:'12px 16px',background:'#fff',border:'1px solid #E8ECF0',borderRadius:10,marginBottom:12}}>
          <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Microsoft Licensing Path</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
            {[
              {id:'ME5',name:'Microsoft 365 E5',color:'#455A64',desc:'Baseline security stack: Entra ID P2, Defender XDR, Sentinel, Purview Information Protection. Covers identity, threat detection, and compliance fundamentals.'},
              {id:'ME5+Copilot',name:'ME5 + Copilot for M365',color:'#1565C0',desc:'Adds Copilot across M365 apps with built-in responsible AI guardrails, content safety, and admin controls. Enables AI-assisted productivity with default governance.'},
              {id:'ME5+A365',name:'ME5 + Copilot + Agent 365',color:'#0078D4',desc:'Adds autonomous agent capabilities: agent identity (Entra Agent ID), agent runtime, tool gateway (MCP), per-agent permissions, human approval gates, and shadow agent discovery.'},
              {id:'ME7',name:'Microsoft 365 E7 (Full Stack)',color:'#6A1B9A',desc:'Complete AI security stack: all E5 + Copilot + Agent 365 capabilities plus advanced AI threat protection, Defender for AI, real-time agent monitoring, and Security Copilot for SOC.'},
            ].map(t => (
              <div key={t.id} style={{padding:'10px 12px',background:'#FAFBFC',borderRadius:8,border:`1px solid ${t.color}22`}}>
                <span style={{fontSize:9,padding:'2px 8px',borderRadius:6,background:t.color+'14',color:t.color,fontWeight:700}}>{t.id}</span>
                <div style={{fontSize:10,fontWeight:600,color:'#0D1B2A',marginTop:4,marginBottom:3}}>{t.name}</div>
                <div style={{fontSize:9,color:'#7A8899',lineHeight:1.4}}>{t.desc}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:8,fontSize:9,color:'#7A8899',textAlign:'center'}}>
            ME5 &#8594; ME5+Copilot &#8594; ME5+Agent 365 &#8594; ME7 — each tier unlocks additional AI security controls and capabilities
          </div>
        </div>
      )}

      {/* Search + view toggle */}
      <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16}}>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search controls, categories, objectives..."
          style={{flex:1,fontSize:12,padding:'8px 12px',border:'1px solid #E0E4E8',borderRadius:8,background:'#fff'}}
        />
        <div style={{display:'flex',gap:2,background:'#F5F7F9',borderRadius:8,padding:2}}>
          {[{id:'domain',label:'By domain'},{id:'layer',label:'By layer'},{id:'flat',label:'All controls'}].map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)} style={{
              fontSize:10,fontWeight:600,padding:'6px 12px',borderRadius:6,border:'none',cursor:'pointer',
              background:viewMode===v.id?'#fff':'transparent',
              color:viewMode===v.id?'#1565C0':'#7A8899',
              boxShadow:viewMode===v.id?'0 1px 3px rgba(0,0,0,0.08)':'none',
            }}>{v.label}</button>
          ))}
        </div>
      </div>

      {/* ── Domain view ── */}
      {viewMode === 'domain' && DOMAINS.map(d => {
        const ctrls = byDomain(d.id);
        const cats = categoriesIn(ctrls);
        const isOpen = expandedDomain === d.id;
        return (
          <div key={d.id} className="card" style={{marginBottom:8,overflow:'hidden'}}>
            <div onClick={() => toggleDomain(d.id)} style={{
              display:'flex',alignItems:'center',gap:14,padding:'14px 18px',cursor:'pointer',
              background: isOpen ? '#F0F6FF' : '#fff',
            }}>
              <span style={{fontSize:12,fontWeight:700,color:d.color,width:28}}>{d.id}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A'}}>{d.name}</div>
                <div style={{fontSize:10,color:'#7A8899'}}>{ctrls.length} controls · {cats.length} categories</div>
              </div>
              <div style={{fontSize:14,color:'#B0BAC5',transform:isOpen?'rotate(90deg)':'none',transition:'transform 0.15s'}}>&#8250;</div>
            </div>
            {isOpen && (
              <div style={{borderTop:'1px solid #EEF0F3'}}>
                {cats.map(cat => {
                  const catCtrls = ctrls.filter(c => c.category === cat);
                  const obj = objectives[cat] || '';
                  return (
                    <div key={cat}>
                      <div style={{padding:'10px 18px 6px',background:'#F8FAFC',borderBottom:'1px solid #EEF0F3'}}>
                        <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A'}}>{cat}</div>
                        {obj && <div style={{fontSize:10,color:'#7A8899',marginTop:2,lineHeight:1.4,fontStyle:'italic'}}>{obj}</div>}
                      </div>
                      {catCtrls.map(c => <ControlRow key={c.id} c={c}/>)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Layer view ── */}
      {viewMode === 'layer' && layers.map(l => {
        const ctrls = byLayer(l.id);
        const isOpen = expandedDomain === l.id;
        return (
          <div key={l.id} className="card" style={{marginBottom:8,overflow:'hidden'}}>
            <div onClick={() => toggleDomain(l.id)} style={{
              display:'flex',alignItems:'center',gap:14,padding:'14px 18px',cursor:'pointer',
              background: isOpen ? '#F0F6FF' : '#fff',
            }}>
              <div style={{width:28,height:28,borderRadius:6,display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,background:l.color+'18',color:l.color}}>{l.order}</div>
              <div style={{flex:1}}>
                <div style={{fontSize:13,fontWeight:600,color:'#0D1B2A'}}>{l.name}</div>
                <div style={{fontSize:10,color:'#7A8899'}}>{l.m365} · {ctrls.length} controls</div>
              </div>
              <div style={{fontSize:14,color:'#B0BAC5',transform:isOpen?'rotate(90deg)':'none',transition:'transform 0.15s'}}>&#8250;</div>
            </div>
            {isOpen && (
              <div style={{borderTop:'1px solid #EEF0F3'}}>
                {ctrls.map(c => <ControlRow key={c.id} c={c}/>)}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Flat view ── */}
      {viewMode === 'flat' && (
        <div className="card" style={{overflow:'hidden'}}>
          <div style={{padding:'10px 14px',background:'#F8F9FA',borderBottom:'1px solid #EEF0F3',display:'grid',gridTemplateColumns:'70px 1fr 80px 80px 24px',gap:8,fontSize:10,fontWeight:600,color:'#7A8899'}}>
            <div>ID</div><div>Statement / Evidence Required</div><div style={{textAlign:'center'}}>Licensing</div><div style={{textAlign:'center'}}>Layer</div><div></div>
          </div>
          {filtered.map(c => <ControlRow key={c.id} c={c}/>)}
          {filtered.length === 0 && (
            <div style={{padding:'24px',textAlign:'center',fontSize:12,color:'#B0BAC5'}}>No controls match your search.</div>
          )}
        </div>
      )}

      {/* Framework references */}
      <div style={{marginTop:24,padding:'16px 18px',background:'#F8F9FA',borderRadius:10,border:'1px solid #EEF0F3'}}>
        <div style={{fontSize:11,fontWeight:600,color:'#0D1B2A',marginBottom:8}}>Framework alignment</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
          {[
            {name:'NIST AI RMF 1.0',  desc:'AI Risk Management Framework — Govern, Map, Measure, Manage functions'},
            {name:'ISO 42001:2023',    desc:'AI Management System — Annex A controls for AI governance'},
            {name:'OWASP LLM Top 10',  desc:'Top 10 security risks for Large Language Model applications'},
            {name:'MITRE ATLAS',       desc:'Adversarial Threat Landscape for AI Systems — tactics and techniques'},
          ].map(f => (
            <div key={f.name} style={{padding:'10px 12px',background:'#fff',borderRadius:8,border:'1px solid #E8ECF0'}}>
              <div style={{fontSize:11,fontWeight:600,color:'#1565C0',marginBottom:2}}>{f.name}</div>
              <div style={{fontSize:10,color:'#7A8899',lineHeight:1.4}}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
